/**
 * @fileoverview
 * Extensões customizadas de componentes para dispositivos Arduino e compatíveis.
 * Estas extensões aparecem na tela "Escolha uma Extensão" e podem ser adicionadas manualmente.
 * 
 * Cada extensão define:
 * - extensionId: ID único da extensão
 * - name: Nome da extensão (pode ser FormattedMessage para i18n)
 * - description: Descrição da extensão
 * - iconURL: URL do ícone (será criado depois)
 * - tag: Tag para filtro (actuator, sensor, display, etc.)
 * - supportDevice: Array de deviceIds compatíveis ou ['*'] para todos
 * - categoryId: ID da categoria que será injetada no toolbox
 * - buildCategoryXml: Função que constrói o XML da categoria a partir das categorias do dispositivo
 */

import React from 'react';
import {FormattedMessage} from 'react-intl';
import {buildCategoryWithBlocks} from './custom-extension-blocks';

import microServoIconURL from './libraries/custom-extensions/microServo/micro-servo.png';
import photoResistorIconURL from './libraries/custom-extensions/photoResistorSensor/photo-resistor.png';
import ultrasonicIconURL from './libraries/custom-extensions/ultrasonicSensor/ultrasonic.png';
import lineIrIconURL from './libraries/custom-extensions/linetracingIRSensor/line-ir.svg';
import dht11IconURL from './libraries/custom-extensions/dht11Sensor/dht11.png';
import lcdI2cIconURL from './libraries/custom-extensions/liquidCrystalI2c/lcd-i2c.png';
import tb6612IconURL from './libraries/custom-extensions/tb6612fng/motor-driver.png';
import l298nIconURL from './libraries/custom-extensions/l298n/l298n.png';
import tcrt5000IconURL from './libraries/custom-extensions/tcrt5000Digital/tcrt5000.png';
import mpu6050IconURL from './libraries/custom-extensions/mpu6050/mpu6050.png';

// Funções auxiliares para construir categorias (serão importadas de make-toolbox-xml.js)
// Por enquanto, definimos aqui para evitar dependência circular

/**
 * Extrai o primeiro bloco de servo (type contém "ServoOutput") do XML de uma categoria.
 */
export const extractServoBlockFromCategoryXml = function (categoryXml) {
    const blockStartMatch = categoryXml.match(/<block\s[^>]*type="[^"]*ServoOutput[^"]*"[^>]*>/);
    if (!blockStartMatch) return null;
    const startIndex = blockStartMatch.index;
    let depth = 0;
    let i = startIndex;
    const openTag = '<block';
    const closeTag = '</block>';
    const openEnd = categoryXml.indexOf('>', startIndex);
    if (openEnd === -1) return null;
    i = openEnd + 1;
    depth = 1;
    while (depth > 0 && i < categoryXml.length) {
        const nextOpen = categoryXml.indexOf(openTag, i);
        const nextClose = categoryXml.indexOf(closeTag, i);
        if (nextClose === -1) break;
        if (nextOpen !== -1 && nextOpen < nextClose) {
            depth++;
            i = nextOpen + openTag.length;
        } else {
            depth--;
            if (depth === 0) {
                return categoryXml.substring(startIndex, nextClose + closeTag.length);
            }
            i = nextClose + closeTag.length;
        }
    }
    return null;
};

const categorySeparator = '<sep gap="36"/>';

/**
 * Constrói o XML da categoria Micro Servo.
 */
export const buildMicroServoCategoryXml = function (servoBlockXml) {
    const categoryName = '%{microServo.category.name}';
    return `<category name="${categoryName}" id="microServo" colour="#E67E22" secondaryColour="#CC6600">
${servoBlockXml}
${categorySeparator}
</category>`;
};

/**
 * Lista de extensões customizadas disponíveis.
 * Cada extensão será exibida na tela "Escolha uma Extensão" como um card.
 * 
 * Formato esperado (baseado na imagem fornecida):
 * - iconURL: URL da imagem do componente
 * - name: Título do componente (em negrito)
 * - description: Descrição breve do componente
 * - version: Versão da extensão (ex: "1.0.0")
 * - author: Autor da extensão (ex: "MAKE" ou "Labcode")
 * - tag: Tag para filtro (actuator, sensor, display, etc.)
 * - supportDevice: Array de deviceIds compatíveis ou ['*'] para todos
 * - categoryId: ID da categoria que será injetada no toolbox
 * - buildCategoryXml: Função que constrói o XML da categoria
 * - featured: true para aparecer como card destacado
 * - programMode: ['upload'] para modo upload, ['realtime'] para realtime
 * - programLanguage: ['block'] para programação em blocos
 */
export const customDeviceExtensions = [
    {
        extensionId: 'microServo',
        name: (
            <FormattedMessage
                defaultMessage="Micro Servo"
                description="Name for the Micro Servo extension"
                id="gui.customExtension.microServo.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="It is a motor designed to operate accurately in response to commands of frequently changing position or speed."
                description="Description for the Micro Servo extension"
                id="gui.customExtension.microServo.description"
            />
        ),
        iconURL: microServoIconURL,
        version: '1.0.0',
        author: 'Labcode',
        tag: 'actuator',
        featured: true,
        supportDevice: ['*'], // Compatível com todos os dispositivos Arduino
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'microServo',
        buildCategoryXml: (categoriesXML) => {
            // Procura o bloco de servo nas categorias do dispositivo
            // Procura em todas as categorias, especialmente na categoria "pin" ou "pins"
            for (const cat of categoriesXML) {
                if (cat.xml) {
                    const servoBlockXml = extractServoBlockFromCategoryXml(cat.xml);
                    if (servoBlockXml) {
                        return buildMicroServoCategoryXml(servoBlockXml);
                    }
                }
            }
            // Se não encontrou, retorna null para usar o fallback
            return null;
        }
    },
    {
        extensionId: 'photoResistorSensor',
        name: (
            <FormattedMessage
                defaultMessage="Photo Resistor Sensor"
                description="Name for the Photo Resistor Sensor extension"
                id="gui.customExtension.photoResistorSensor.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="A sensor that can measure the brightness of the surrounding environment. The brighter the surroundings, the smaller the measurement."
                description="Description for the Photo Resistor Sensor extension"
                id="gui.customExtension.photoResistorSensor.description"
            />
        ),
        iconURL: photoResistorIconURL,
        version: '1.0.0',
        author: 'MAKE',
        tag: 'sensor',
        featured: true,
        supportDevice: ['*'],
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'photoResistorSensor',
        buildCategoryXml: (categoriesXML) => {
            // TODO: Implementar blocos específicos para Photo Resistor
            // Por enquanto, retorna null para criar categoria vazia via fallback
            return null;
        }
    },
    {
        extensionId: 'ultrasonicSensor',
        name: (
            <FormattedMessage
                defaultMessage="Ultrasonic Sensor"
                description="Name for the Ultrasonic Sensor extension"
                id="gui.customExtension.ultrasonicSensor.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="It is a sensor that generates ultrasonic waves and calculates the time it takes to hit an object and return to it to measure the distance to the object."
                description="Description for the Ultrasonic Sensor extension"
                id="gui.customExtension.ultrasonicSensor.description"
            />
        ),
        iconURL: ultrasonicIconURL,
        version: '1.0.0',
        author: 'MAKE',
        tag: 'sensor',
        featured: true,
        supportDevice: ['*'],
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'ultrasonicSensor',
        buildCategoryXml: (categoriesXML) => {
            const categoryName = '%{ultrasonicSensor.category.name}';
            return buildCategoryWithBlocks('ultrasonicSensor', categoryName, '#4C97FF', '#3373CC');
        }
    },
    {
        extensionId: 'linetracingIRSensor',
        name: (
            <FormattedMessage
                defaultMessage="Linetracing IR sensor"
                description="Name for the Linetracing IR sensor extension"
                id="gui.customExtension.linetracingIRSensor.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="This is a sensor module that recognizes black/other colors to drive the line tracking robot."
                description="Description for the Linetracing IR sensor extension"
                id="gui.customExtension.linetracingIRSensor.description"
            />
        ),
        iconURL: lineIrIconURL,
        version: '1.0.0',
        author: 'MAKE',
        tag: 'sensor',
        featured: true,
        supportDevice: ['*'],
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'linetracingIRSensor',
        buildCategoryXml: (categoriesXML) => {
            // TODO: Implementar blocos específicos para Linetracing IR sensor
            return null;
        }
    },
    {
        extensionId: 'dht11Sensor',
        name: (
            <FormattedMessage
                defaultMessage="Dht11_sensor"
                description="Name for the DHT11 sensor extension"
                id="gui.customExtension.dht11Sensor.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="It is a sensor that can measure the ambient temperature and humidity, which is the amount of water vapor in the air."
                description="Description for the DHT11 sensor extension"
                id="gui.customExtension.dht11Sensor.description"
            />
        ),
        iconURL: dht11IconURL,
        version: '1.0.0',
        author: 'MAKE',
        tag: 'sensor',
        featured: true,
        supportDevice: ['*'],
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'dht11Sensor',
        buildCategoryXml: (categoriesXML) => {
            const categoryName = '%{dht11Sensor.category.name}';
            return buildCategoryWithBlocks('dht11Sensor', categoryName, '#4C97FF', '#3373CC');
        }
    },
    {
        extensionId: 'liquidCrystalI2c',
        name: (
            <FormattedMessage
                defaultMessage="Liquid Crystal 16x2 I2C"
                description="Name for the Liquid Crystal 16x2 I2C display extension"
                id="gui.customExtension.liquidCrystalI2c.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="Display LCD 16 columns by 2 rows connected via I2C (e.g. PCF8574). Use blocks to initialize, print text at position, and clear the screen."
                description="Description for the Liquid Crystal 16x2 I2C extension"
                id="gui.customExtension.liquidCrystalI2c.description"
            />
        ),
        iconURL: lcdI2cIconURL,
        version: '1.0.0',
        author: 'Labcode',
        tag: 'display',
        featured: true,
        supportDevice: ['*'],
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'liquidCrystalI2c',
        buildCategoryXml: (categoriesXML) => {
            const categoryName = '%{liquidCrystalI2c.category.name}';
            return buildCategoryWithBlocks('liquidCrystalI2c', categoryName, '#0FBD8C', '#0DA57A');
        }
    },
    {
        extensionId: 'tb6612fng',
        name: (
            <FormattedMessage
                defaultMessage="Ponte H TB6612FNG"
                description="Name for the TB6612FNG H-Bridge motor driver extension"
                id="gui.customExtension.tb6612fng.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="Driver dual de motores DC (ponte H). Configure os pinos PWMA, AIN1, AIN2 (motor A) e PWMB, BIN1, BIN2 (motor B), depois use os blocos para controlar direção e velocidade."
                description="Description for the TB6612FNG extension"
                id="gui.customExtension.tb6612fng.description"
            />
        ),
        iconURL: tb6612IconURL,
        version: '1.0.0',
        author: 'Labcode',
        tag: 'actuator',
        featured: true,
        supportDevice: ['*'],
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'tb6612fng',
        buildCategoryXml: (categoriesXML) => {
            const categoryName = '%{tb6612fng.category.name}';
            return buildCategoryWithBlocks('tb6612fng', categoryName, '#E67E22', '#CC6600');
        }
    },
    {
        extensionId: 'l298n',
        name: (
            <FormattedMessage
                defaultMessage="Ponte H L298N"
                description="Name for the L298N H-Bridge motor driver extension"
                id="gui.customExtension.l298n.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="Driver dual de motores DC (ponte H L298N). Configure ENA, IN1, IN2 (motor A) e ENB, IN3, IN4 (motor B), depois use os blocos para controlar direção e velocidade."
                description="Description for the L298N extension"
                id="gui.customExtension.l298n.description"
            />
        ),
        iconURL: l298nIconURL,
        version: '1.0.0',
        author: 'Labcode',
        tag: 'actuator',
        featured: true,
        supportDevice: ['*'],
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'l298n',
        buildCategoryXml: (categoriesXML) => {
            const categoryName = '%{l298n.category.name}';
            return buildCategoryWithBlocks('l298n', categoryName, '#C0392B', '#A93226');
        }
    },
    {
        extensionId: 'tcrt5000Digital',
        name: (
            <FormattedMessage
                defaultMessage="Módulo TCRT5000 Digital"
                description="Name for the TCRT5000 digital sensor extension"
                id="gui.customExtension.tcrt5000Digital.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="Sensor infravermelho reflexivo com saída digital. Usado em seguidor de linha ou detecção de obstáculos; retorna verdadeiro/falso conforme o ajuste do potenciômetro no módulo."
                description="Description for the TCRT5000 digital extension"
                id="gui.customExtension.tcrt5000Digital.description"
            />
        ),
        iconURL: tcrt5000IconURL,
        version: '1.0.0',
        author: 'Labcode',
        tag: 'sensor',
        featured: true,
        supportDevice: ['*'],
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'tcrt5000Digital',
        buildCategoryXml: (categoriesXML) => {
            const categoryName = '%{tcrt5000Digital.category.name}';
            return buildCategoryWithBlocks('tcrt5000Digital', categoryName, '#4C97FF', '#3373CC');
        }
    },
    {
        extensionId: 'mpu6050',
        name: (
            <FormattedMessage
                defaultMessage="Módulo MPU-6050"
                description="Name for the MPU-6050 accelerometer/gyroscope extension"
                id="gui.customExtension.mpu6050.name"
            />
        ),
        description: (
            <FormattedMessage
                defaultMessage="Acelerômetro e giroscópio 6 eixos (I2C). Leia aceleração X/Y/Z, velocidade angular e temperatura."
                description="Description for the MPU-6050 extension"
                id="gui.customExtension.mpu6050.description"
            />
        ),
        iconURL: mpu6050IconURL,
        version: '1.0.0',
        author: 'Labcode',
        tag: 'sensor',
        featured: true,
        supportDevice: ['*'],
        programMode: ['upload'],
        programLanguage: ['block'],
        categoryId: 'mpu6050',
        buildCategoryXml: (categoriesXML) => {
            const categoryName = '%{mpu6050.category.name}';
            return buildCategoryWithBlocks('mpu6050', categoryName, '#4C97FF', '#3373CC');
        }
    }
];

/**
 * Obtém uma extensão customizada por ID.
 */
export const getCustomExtensionById = (extensionId) => {
    return customDeviceExtensions.find(ext => ext.extensionId === extensionId);
};

/**
 * Verifica se uma extensão customizada está carregada.
 */
export const isCustomExtensionLoaded = (extensionId, loadedExtensions) => {
    return loadedExtensions && loadedExtensions.includes(extensionId);
};
